import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import logo from "../assets/image/logo_web.png";
import FormAutomationPresetEditor from "../components/FormAutomationPresetEditor";
import { getCurrentUserCompanyId, getCurrentUserData } from "../utils/userUtils";
import { hasPermission } from "../utils/permissions";
import {
  AI_IMPORT_PRESETS,
  formatStarterFieldTemplates,
  getBuiltInPreset,
  parseStarterFieldTemplates,
  resolvePresetSelection,
} from "../utils/formAutomationPresets";

const EMPTY_PRESET_FORM = {
  name: "",
  basePresetKey: "general",
  extraInstructions: "",
  starterSections: "",
  starterFieldTemplates: "",
};

export default function FormAutomationPresetLibrary() {
  const navigate = useNavigate();
  const [currentUserData, setCurrentUserData] = useState(null);
  const [currentCompanyId, setCurrentCompanyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("general");
  const [companyDefaultPreset, setCompanyDefaultPreset] = useState("general");
  const [customPresets, setCustomPresets] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [presetForm, setPresetForm] = useState(EMPTY_PRESET_FORM);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const [userData, companyId] = await Promise.all([
          getCurrentUserData(),
          getCurrentUserCompanyId(),
        ]);

        if (!active) return;

        setCurrentUserData(userData);
        setCurrentCompanyId(companyId || "");

        if (!companyId || !hasPermission(userData, "create_forms")) {
          return;
        }

        const getSettings = httpsCallable(functions, "getFormAutomationSettings");
        const result = await getSettings({ companyId });

        if (!active) return;

        const defaultPresetSelection = result?.data?.defaultPresetSelection || result?.data?.defaultPresetKey || "general";
        const nextCustomPresets = Array.isArray(result?.data?.customPresets) ? result.data.customPresets : [];
        setCompanyDefaultPreset(defaultPresetSelection);
        setSelectedPreset(defaultPresetSelection);
        setCustomPresets(nextCustomPresets);
      } catch (error) {
        console.error("Failed to load preset library:", error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const canManagePresets = hasPermission(currentUserData, "create_forms");
  const resolvedDefault = useMemo(
    () => resolvePresetSelection(companyDefaultPreset, customPresets),
    [companyDefaultPreset, customPresets]
  );
  const resolvedSelected = useMemo(
    () => resolvePresetSelection(selectedPreset, customPresets),
    [selectedPreset, customPresets]
  );

  const openCreateEditor = () => {
    const seed = resolvePresetSelection(selectedPreset, customPresets);
    setEditingPresetId(null);
    setPresetForm({
      name: seed.customPresetName || "",
      basePresetKey: seed.presetKey,
      extraInstructions: seed.customPresetInstructions || "",
      starterSections: (seed.customPresetStarterSections || []).join(", "),
      starterFieldTemplates: formatStarterFieldTemplates(seed.customPresetStarterFieldTemplates || []),
    });
    setEditorOpen(true);
  };

  const openEditEditor = (preset) => {
    setEditingPresetId(preset.id);
    setPresetForm({
      name: preset.name || "",
      basePresetKey: preset.basePresetKey || "general",
      extraInstructions: preset.extraInstructions || "",
      starterSections: (preset.starterSections || []).join(", "),
      starterFieldTemplates: formatStarterFieldTemplates(preset.starterFieldTemplates || []),
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingPresetId(null);
    setPresetForm(EMPTY_PRESET_FORM);
  };

  const persistSettings = async ({ nextDefaultSelection = companyDefaultPreset, nextCustomPresets = customPresets }) => {
    if (!currentCompanyId) return null;
    const setSettings = httpsCallable(functions, "setFormAutomationSettings");
    return setSettings({
      companyId: currentCompanyId,
      defaultPresetKey: resolvePresetSelection(nextDefaultSelection, nextCustomPresets).presetKey,
      defaultPresetSelection: nextDefaultSelection,
      customPresets: nextCustomPresets,
    });
  };

  const saveCompanyDefaultPreset = async (selection = selectedPreset) => {
    if (!currentCompanyId) return;
    try {
      setSaving(true);
      const result = await persistSettings({ nextDefaultSelection: selection, nextCustomPresets: customPresets });
      setCompanyDefaultPreset(result?.data?.defaultPresetSelection || selection);
      setSelectedPreset(selection);
    } catch (error) {
      console.error("Failed to save company default preset:", error);
      alert(error.message || "Could not save company default preset.");
    } finally {
      setSaving(false);
    }
  };

  const saveCustomPreset = async () => {
    if (!currentCompanyId) return;
    const trimmedName = String(presetForm.name || "").trim();
    if (!trimmedName) {
      alert("Preset name is required.");
      return;
    }

    const starterSections = String(presetForm.starterSections || "")
      .split(",")
      .map((section) => section.trim())
      .filter(Boolean)
      .slice(0, 12);
    const starterFieldTemplates = parseStarterFieldTemplates(presetForm.starterFieldTemplates);

    const nextPreset = {
      id: editingPresetId || `preset_${Date.now()}`,
      name: trimmedName,
      basePresetKey: presetForm.basePresetKey || "general",
      extraInstructions: String(presetForm.extraInstructions || "").trim(),
      starterSections,
      starterFieldTemplates,
    };

    const nextCustomPresets = editingPresetId
      ? customPresets.map((preset) => (preset.id === editingPresetId ? nextPreset : preset))
      : [...customPresets, nextPreset];

    try {
      setSaving(true);
      await persistSettings({ nextCustomPresets });
      setCustomPresets(nextCustomPresets);
      setSelectedPreset(`custom:${nextPreset.id}`);
      closeEditor();
    } catch (error) {
      console.error("Failed to save custom preset:", error);
      alert(error.message || "Could not save company preset.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomPreset = async (presetId) => {
    if (!currentCompanyId) return;
    const nextCustomPresets = customPresets.filter((preset) => preset.id !== presetId);
    const nextDefaultSelection = companyDefaultPreset === `custom:${presetId}` ? "general" : companyDefaultPreset;
    const nextSelectedPreset = selectedPreset === `custom:${presetId}` ? "general" : selectedPreset;

    try {
      setSaving(true);
      await persistSettings({ nextDefaultSelection, nextCustomPresets });
      setCustomPresets(nextCustomPresets);
      setCompanyDefaultPreset(nextDefaultSelection);
      setSelectedPreset(nextSelectedPreset);
    } catch (error) {
      console.error("Failed to delete custom preset:", error);
      alert(error.message || "Could not delete company preset.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-slate-950/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10">
              <img src={logo} alt="BuildMyChecks" className="h-9 w-9" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/70">BuildMyChecks AI</p>
              <h1 className="text-2xl font-black text-white">Preset Library</h1>
              <p className="text-sm text-white/60">Manage reusable AI document-to-check import presets for your company.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
            >
              Back to dashboard
            </button>
            {canManagePresets && (
              <button
                type="button"
                onClick={openCreateEditor}
                className="rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 px-5 py-2 text-sm font-bold text-slate-950 shadow-lg transition hover:brightness-110"
              >
                New company preset
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        {!canManagePresets ? (
          <div className="rounded-3xl border border-amber-300/20 bg-amber-500/10 p-8 text-amber-50">
            <h2 className="text-xl font-bold">Preset library access is restricted</h2>
            <p className="mt-2 text-sm text-amber-100/80">
              You need form creation permission to manage AI import presets for this company.
            </p>
          </div>
        ) : loading ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-white/70">
            Loading preset library...
          </div>
        ) : (
          <div className="grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
            <section className="space-y-8">
              <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-r from-cyan-500/15 via-blue-500/10 to-fuchsia-500/15 p-6 shadow-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-200/70">Current company default</p>
                <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-3xl font-black text-white">{resolvedDefault.label}</h2>
                    <p className="mt-2 max-w-2xl text-sm text-white/70">
                      Base preset: {getBuiltInPreset(resolvedDefault.presetKey).label}
                      {resolvedDefault.customPresetName ? ` · Custom preset: ${resolvedDefault.customPresetName}` : ""}
                    </p>
                    {resolvedDefault.customPresetInstructions && (
                      <p className="mt-3 text-sm text-white/65">{resolvedDefault.customPresetInstructions}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard")}
                    className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
                  >
                    Use in form builder
                  </button>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-black text-white">Built-in preset pack</h2>
                    <p className="mt-1 text-sm text-white/60">Choose the AI drafting style that best matches the uploaded document.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => saveCompanyDefaultPreset(selectedPreset)}
                    disabled={saving || selectedPreset === companyDefaultPreset}
                    className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Set selected as default"}
                  </button>
                </div>
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  {AI_IMPORT_PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => setSelectedPreset(preset.key)}
                      className={`rounded-2xl border p-5 text-left transition-all ${
                        selectedPreset === preset.key
                          ? "border-cyan-400/60 bg-cyan-400/10 shadow-lg shadow-cyan-500/10"
                          : "border-white/10 bg-slate-950/40 hover:bg-white/5"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-lg font-bold text-white">{preset.label}</h3>
                        {companyDefaultPreset === preset.key && (
                          <span className="rounded-full border border-emerald-300/30 bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-100">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-white/65">{preset.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-black text-white">Company preset library</h2>
                    <p className="mt-1 text-sm text-white/60">Save named import playbooks for specific document types, teams, or industries.</p>
                  </div>
                  <button
                    type="button"
                    onClick={openCreateEditor}
                    className="rounded-full border border-fuchsia-300/30 bg-fuchsia-400/10 px-4 py-2 text-sm font-semibold text-fuchsia-100 transition hover:bg-fuchsia-400/20"
                  >
                    Create preset
                  </button>
                </div>

                {customPresets.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-dashed border-white/10 bg-slate-950/40 p-6 text-sm text-white/55">
                    No company presets yet. Create one for routines like weekly audits, cleaning rounds, opening checks, or staged workflows.
                  </div>
                ) : (
                  <div className="mt-5 grid gap-4 lg:grid-cols-2">
                    {customPresets.map((preset) => {
                      const selectionValue = `custom:${preset.id}`;
                      const isSelected = selectedPreset === selectionValue;
                      const isDefault = companyDefaultPreset === selectionValue;

                      return (
                        <div
                          key={preset.id}
                          className={`rounded-2xl border p-5 transition-all ${
                            isSelected
                              ? "border-fuchsia-400/60 bg-fuchsia-400/10 shadow-lg shadow-fuchsia-500/10"
                              : "border-white/10 bg-slate-950/40"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedPreset(selectionValue)}
                            className="w-full text-left"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-lg font-bold text-white">{preset.name}</h3>
                              {isDefault && (
                                <span className="rounded-full border border-emerald-300/30 bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-100">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="mt-2 text-sm text-white/65">
                              Base preset: {getBuiltInPreset(preset.basePresetKey).label}
                            </p>
                            {Array.isArray(preset.starterSections) && preset.starterSections.length > 0 && (
                              <p className="mt-2 text-sm text-white/60">
                                Starter sections: {preset.starterSections.join(", ")}
                              </p>
                            )}
                            {Array.isArray(preset.starterFieldTemplates) && preset.starterFieldTemplates.length > 0 && (
                              <p className="mt-2 text-sm text-white/55">
                                Starter fields: {preset.starterFieldTemplates.map((template) => `${template.sectionTitle} -> ${template.fields.join(", ")}`).join(" | ")}
                              </p>
                            )}
                            {preset.extraInstructions && (
                              <p className="mt-3 line-clamp-4 text-sm text-white/55">{preset.extraInstructions}</p>
                            )}
                          </button>

                          <div className="mt-4 flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => saveCompanyDefaultPreset(selectionValue)}
                              disabled={saving || isDefault}
                              className="rounded-full border border-emerald-300/30 px-3 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/15 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Make default
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditEditor(preset)}
                              className="rounded-full border border-cyan-300/30 px-3 py-2 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-400/15"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteCustomPreset(preset.id)}
                              className="rounded-full border border-rose-300/30 px-3 py-2 text-xs font-semibold text-rose-100 transition hover:bg-rose-400/15"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <aside className="space-y-6">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-200/70">Selected preset</p>
                <h2 className="mt-3 text-2xl font-black text-white">{resolvedSelected.label}</h2>
                <p className="mt-2 text-sm text-white/65">
                  Base preset: {getBuiltInPreset(resolvedSelected.presetKey).label}
                </p>
                {resolvedSelected.customPresetInstructions && (
                  <p className="mt-4 text-sm text-white/60">{resolvedSelected.customPresetInstructions}</p>
                )}
                {resolvedSelected.customPresetStarterSections?.length > 0 && (
                  <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Starter sections</p>
                    <p className="mt-2 text-sm text-white/75">{resolvedSelected.customPresetStarterSections.join(", ")}</p>
                  </div>
                )}
                {resolvedSelected.customPresetStarterFieldTemplates?.length > 0 && (
                  <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Starter field skeletons</p>
                    <p className="mt-2 text-sm text-white/75">
                      {resolvedSelected.customPresetStarterFieldTemplates
                        .map((template) => `${template.sectionTitle} -> ${template.fields.join(", ")}`)
                        .join(" | ")}
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => saveCompanyDefaultPreset(selectedPreset)}
                  disabled={saving || selectedPreset === companyDefaultPreset}
                  className="mt-6 w-full rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 px-5 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Set selected preset as company default"}
                </button>
              </div>

              {editorOpen && (
                <FormAutomationPresetEditor
                  form={presetForm}
                  setForm={setPresetForm}
                  onCancel={closeEditor}
                  onSave={saveCustomPreset}
                  saving={saving}
                  title={editingPresetId ? "Edit company preset" : "Create company preset"}
                  saveLabel={editingPresetId ? "Save changes" : "Create preset"}
                />
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
